import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import QRBlock from "@/components/QRBlock";
import { Button } from "@/components/ui/button";
import { Smartphone, Gamepad2, Monitor, Users, Gamepad, CheckCircle2, Trophy, Zap, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Home({ event, slug }) {
  const [stats, setStats] = useState(null);
  const origin = window.location.origin;
  const playerUrl = `${origin}/e/${slug}`;
  const modUrl = `${origin}/mod/${slug}`;
  const displayUrl = `${origin}/display/${slug}`;

  useEffect(() => { api.get(`/events/${slug}/analytics`).then((r) => setStats(r.data)).catch(() => {}); }, [slug]);

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied!"); };

  const checklist = [
    { l: "Arena created", done: true },
    { l: "Add Challenge Zones", done: (stats?.zone_count || 0) > 0 },
    { l: "Register a Player", done: (stats?.total_players || 0) > 0 },
    { l: "Award some Cash", done: (stats?.total_xp || 0) > 0 },
    { l: "Test the Big Screen", done: false },
    { l: "Share your QR", done: false },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head font-black text-3xl mb-1">Your Arena is ready! 🎯</h1>
        <p className="text-muted-foreground">Share these three experiences — no technical setup needed.</p>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[{ l: "Registered", v: stats?.total_players, i: Users, c: "text-neon-blue" },
          { l: "Active now", v: stats?.active_players, i: Zap, c: "text-neon-green" },
          { l: "Challenges done", v: stats?.completed_challenges, i: Gamepad, c: "text-gold" },
          { l: "Total Cash", v: stats?.total_xp, i: Trophy, c: "text-primary" }].map((s, i) => { const I = s.i; return (
          <div key={i} className="brut p-5">
            <I className={`w-5 h-5 ${s.c} mb-2`} />
            <div className="font-mono-num font-extrabold text-3xl">{s.v ?? "—"}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.l}</div>
          </div>
        ); })}
      </div>

      {/* share arena */}
      <div>
        <h2 className="font-head font-black text-2xl mb-4">Share Your Arena</h2>
        <div className="grid md:grid-cols-3 gap-5">
          <div className="brut-primary bg-card p-6 rounded-2xl">
            <div className="flex items-center gap-2 font-head font-bold text-lg mb-1"><Smartphone className="w-5 h-5 text-primary" />Player Arena</div>
            <p className="text-xs text-muted-foreground mb-4">Scan to Enter</p>
            <QRBlock value={playerUrl} label="player" testid="player-qr" />
            <Button variant="outline" className="w-full rounded-full mt-3" onClick={() => window.open(playerUrl, "_blank")}>Open <ExternalLink className="w-3.5 h-3.5 ml-1" /></Button>
          </div>

          <div className="brut p-6">
            <div className="flex items-center gap-2 font-head font-bold text-lg mb-1"><Gamepad2 className="w-5 h-5 text-neon-green" />Game Master Console</div>
            <p className="text-xs text-muted-foreground mb-4">For moderators running games</p>
            <div className="brut p-3 text-sm font-mono-num break-all mb-3">{modUrl}</div>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1 rounded-full" onClick={() => copy(modUrl)} data-testid="copy-mod"><Copy className="w-4 h-4 mr-1" />Copy</Button>
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => window.open(modUrl, "_blank")}>Open</Button>
            </div>
          </div>

          <div className="brut p-6">
            <div className="flex items-center gap-2 font-head font-bold text-lg mb-1"><Monitor className="w-5 h-5 text-neon-blue" />Big Screen Display</div>
            <p className="text-xs text-muted-foreground mb-4">Open on the laptop connected to your TV / projector</p>
            <div className="brut p-3 text-sm font-mono-num break-all mb-3">{displayUrl}</div>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1 rounded-full" onClick={() => copy(displayUrl)} data-testid="copy-display"><Copy className="w-4 h-4 mr-1" />Copy</Button>
              <Button className="flex-1 rounded-full font-bold" onClick={() => window.open(displayUrl, "_blank")} data-testid="open-display">Open Display</Button>
            </div>
          </div>
        </div>
      </div>

      {/* onboarding checklist */}
      <div className="brut p-6">
        <h2 className="font-head font-bold text-lg mb-4">Get set up</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {checklist.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className={`w-5 h-5 ${c.done ? "text-neon-green" : "text-muted-foreground/40"}`} />
              <span className={c.done ? "" : "text-muted-foreground"}>{c.l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
